import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Entity } from '@app/core/model/entities/entity';
import { IIndicatorWidget } from '@app/shared/components/indicator-status-panel/indicator-status-panel.component';
import { getLocale, getValue } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { IStatusPanelAngularComp } from 'ag-grid-angular';
import { GridApi, IStatusPanelParams } from 'ag-grid-community';
import { from, mergeMap, Observable, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable()
export abstract class ServerSideIndicatorStatusPanelComponent implements IStatusPanelAngularComp {

  public gridApi: GridApi<Entity>;
  public indicators: IIndicatorWidget[] = [];
  protected currentGridFilters: Filter[] = [];
  protected currentGridSearch: string = null;
  protected isAnyFilterPresent = signal(false);

  public updateFilteredValuesSubject = new Subject<{ filters: Filter[], search?: string }>();
  private destroyRef = inject(DestroyRef);

  /**
   * Set gridApi and add listen for row updates.
   */
  public agInit(params: IStatusPanelParams<Entity>): void {
    this.gridApi = params.api;

    this.updateFilteredValuesSubject
      .pipe(
        tap(({filters, search}) => {
          // Set current filters applied to the grid.
          // This array contains all the filters to apply on entities in order to only take filtered rows of the grid into account in calculations.
          this.currentGridFilters = filters;
          // Set search filter applied to the grid.
          this.currentGridSearch = search;
          this.isAnyFilterPresent.set(false);
        }),
        switchMap(({filters, search}) =>
          from(this.indicators)
            .pipe(
              mergeMap(indicator => {
                const selectParams = {
                  groups: [''],
                  filters: [...indicator.filters, ...filters],
                  ...(search && {search: search}),
                  aggregations: [indicator.aggregation],
                  locale: getLocale()
                };

                return this.getAggregationResult(selectParams)
                  .pipe(
                    tap(aggResult =>
                      indicator.filteredValue = aggResult ? getValue(aggResult, indicator.aggregation.field.split('.')) : 0
                    )
                  );
              }),
              tap({complete: () => this.isAnyFilterPresent.set(this.gridApi?.isAnyFilterPresent() || this.currentGridSearch !== null)})
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Set total indicators for all elements in the grid, even those which are not charged yet due to pagination.
   *
   * @param indicators Indicators to calculate
   * @param defaultFilters A list of default filters applied to entities when computing the total and filtered indicator values.
   * These filters are primarily used to restrict entities based on their related asset ID.
   */
  public setIndicators(indicators: IIndicatorWidget[], defaultFilters: Filter[]): void {
    indicators.forEach(i => i.filters.push(...defaultFilters));
    this.indicators = indicators;
    this.setTotalValues();
  }

  /**
   * Update indicators (total values and filtered values).
   */
  protected updateIndicatorsValues(): void {
    this.setTotalValues();
    this.updateFilteredValuesSubject.next({filters: this.currentGridFilters, search: this.currentGridSearch});
  }

  /**
   * Calculate indicators' total value.
   */
  private setTotalValues(): void {
    this.indicators.forEach(indicator => {
      const selectParams = {
        groups: [''],
        filters: indicator.filters,
        aggregations: [indicator.aggregation],
        locale: getLocale()
      };

      this.getAggregationResult(selectParams)
        .subscribe(aggResult =>
          indicator.totalValue = aggResult ? getValue(aggResult, indicator.aggregation.field.split('.')) : 0
        );
    });
  }

  /**
   * Get aggregation result.
   * @param selectParams The parameters for the query. These include filtering, and aggregating.
   * @return Observable<Entity> Result of the aggregation query.
   */
  protected abstract getAggregationResult(selectParams: SelectParams): Observable<Entity>;
}
